import React, {createContext, useReducer, useEffect} from 'react';
import xor from 'lodash.xor';
import pick from "lodash.pick";
import AppReducer, {
  SET_DEFAULT_PRESETS,
  SET_BUNDLES,
  SET_OPTIONS,
  SET_PRODUCTS,
  SET_SELECTED_OPTIONS,
  TOGGLE_STREAM,
  TOGGLE_CONFIGURATION_PORTAL,
  TOGGLE_PRODUCT_PORTAL,
  TOGGLE_STICKY_PANEL,
  TOGGLE_PRODUCT_DETAILS,
  TOGGLE_INTERACTIONS_PORTAL,
  SET_ACTIVE_BUNDLE_ID,
  SET_ACTIVE_TAB_ID,
  SET_STREAM_STORE,
  SET_SESSION_ID,
  SET_QUEUE_DATA, SET_PRODUCT_DETAILS, SET_CONNECTION_STATUS
} from './reducers/app.reducer';
import {makeConfiguratorLink, reduceOptions, isHardCoded} from "../helpers";
import PropTypes from "prop-types";
import {
  SYSTEM_ID_CHANGE_BUNDLE,
  SYSTEM_ID_CHANGE_SINGLE,
  SYSTEM_ID_TOGGLE_DESK_CAMERA,
  SYSTEM_ID_ICUE,
  SYSTEM_ID_RESET_CAMERA,
  SYSTEM_ID_RESET_CONFIG,
  SYSTEM_ID_TOGGLE_DOOR,
  SYSTEM_ID_TOGGLE_INSPECT,
  SYSTEM_ID_TOGGLE_POWER,
} from "../contants/presets";
import {initialActionsStore} from '../hooks/useStreamActionsSingle';
import {TABS, TEXT_GOMLVL_LONG} from '../contants/common';

export const ISOLATED_PRODUCT = 'isolate';
export const INTRO_MOVIE_FINISHED = 'intro_movie';

export const RESPONSE_ISOLATE_ACTIVATED = 'isolate_item_activated';
export const RESPONSE_ISOLATE_DISABLED = 'isolate_item_disabled';
export const RESPONSE_MOVIE_FINISHED = 'intro_movie_finished';

export const ACTION_ISOLATE_ITEM = 'action_isolate_item';
export const ACTION_ISOLATE_ITEM_DISABLED = 'action_isolate_item_disabled';

export const ACTIONS_DISABLED = 'actions_disabled';
export const ACTION_CHANGE_BUNDLE = 'action_change_bundle';

export const CONNECTION_STATUS_DEFAULT = 'default';
export const CONNECTION_STATUS_RECONNECTION = 'reconnection';
export const CONNECTION_STATUS_DISCONNECTED = 'disconnected';

export const dataKeysForSend = [
  SYSTEM_ID_CHANGE_BUNDLE,
  SYSTEM_ID_CHANGE_SINGLE,
  SYSTEM_ID_ICUE,
  SYSTEM_ID_RESET_CAMERA,
  SYSTEM_ID_RESET_CONFIG,
  SYSTEM_ID_TOGGLE_POWER,
  SYSTEM_ID_TOGGLE_DOOR,
  SYSTEM_ID_TOGGLE_INSPECT,
  SYSTEM_ID_TOGGLE_DESK_CAMERA,
]

export const validStreamStoreIDS = [
  ACTIONS_DISABLED,
  ACTION_CHANGE_BUNDLE,
  ISOLATED_PRODUCT,
  INTRO_MOVIE_FINISHED,
  ...dataKeysForSend,
];

export const STORAGE_KEY = 'frontend-storage';
const REDIRECT_LINK = 'https://www.mifcom.de/getonmylvl-cid287?utm_source=arcware.cloud&utm_medium=partner&utm_campaign=goml_3D';
const STORED_KEYS = [
  /* @TODO Occurs bug with the absence panels on configurator page */
  // 'isAllowStream',
  'streamStore',
  'sessionId',
  'activeTabId',
  'activeBundleId',
  'selectedOptions',
  'productDetailsId',
];

const getStateFromStorage = () => {
  return pick(
    JSON.parse(window.localStorage.getItem(STORAGE_KEY)),
    STORED_KEYS
  )
};
const setStateToStorage = state => {
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(pick(state, STORED_KEYS))
  )
};

export const initialStreamStore = () => ({
  ...initialActionsStore,
  [ACTIONS_DISABLED]: [],
  [ACTION_CHANGE_BUNDLE]: null,
  [ISOLATED_PRODUCT]: null,
  [INTRO_MOVIE_FINISHED]: true,
});

const INITIAL_STATE = () => {
  const initialState = {
    streamStore: initialStreamStore(),
    sessionId: null,
    queueData: {
      index: undefined,
      queueLength: undefined,
      averageWaitTime: undefined,
      estimatedWaitTime: undefined,
    },
    isAllowStream: false,
    isConfiguratorPortalOpen: false,
    isStickyPanelCollapsed: true,
    isProductDetailsOpen: false,
    isProductPortalOpen: false,
    isInteractionsPortalOpen: false,
    isOpenPortal: false,
    bundles: [],
    options: [],
    products: [],
    selectedOptions: null,
    activeTabId: TABS[0].name,
    activeBundleId: null,
    defaultPresets: {},
    productDetailsId: null,
    connectionStatus: CONNECTION_STATUS_DEFAULT,
  };

  try {
    const storedWebRTC = JSON.parse(localStorage.getItem('sessionId'));
    const storedStream = getStateFromStorage();
    const sessionWebRTC = storedWebRTC?.value?.session;
    const sessionUI = storedStream?.sessionId;

    if(sessionWebRTC === sessionUI) {
      initialState.sessionId = sessionUI;
    }
  } catch (e) {
    console.warn('Detecting different session error', e);
  }

  return {
    ...initialState,
    ...getStateFromStorage(),
  }
};

const AppContext = createContext(null);

export function Provider({children}) {
  const [state, setState] = useReducer(AppReducer, INITIAL_STATE(), INITIAL_STATE)

  useEffect(() => {
    setStateToStorage(state);
  }, [state])

  const scope = {
    streamStore: state.streamStore,
    sessionId: state.sessionId,
    queueData: state.queueData,
    isAllowStream: state.isAllowStream,
    isConfiguratorAvailable: !!(state.bundles.length
      && state.options.length
      && state.isAllowStream),
    isConfiguratorPortalOpen: state.isConfiguratorPortalOpen,
    isStickyPanelCollapsed: state.isStickyPanelCollapsed,
    isProductPortalOpen: state.isProductPortalOpen,
    isProductDetailsOpen: state.isProductDetailsOpen,
    isInteractionsPortalOpen: state.isInteractionsPortalOpen,
    bundles: state.bundles,
    options: state.options,
    products: state.products,
    selectedOptions: state.selectedOptions,
    activeTabId: state.activeTabId,
    activeBundleId: state.activeBundleId,
    defaultPresets: state.defaultPresets,
    productDetailsId: state.productDetailsId,
    connectionStatus: state.connectionStatus,

    setStreamStore: payload => setState({type: SET_STREAM_STORE, payload}),
    setConnectionStatus: payload => setState({type: SET_CONNECTION_STATUS, payload}),
    setSessionId: payload => setState({type: SET_SESSION_ID, payload}),
    setQueueData: payload => setState({type: SET_QUEUE_DATA, payload}),
    setBundles: payload => setState({type: SET_BUNDLES, payload}),
    setOptions: payload => setState({type: SET_OPTIONS, payload}),
    setProducts: payload => setState({type: SET_PRODUCTS, payload}),
    setSelectedOptions: data => setState({
      type: SET_SELECTED_OPTIONS,
      payload: Array.isArray(data) ? reduceOptions(data, state.options) : data,
    }),
    setProductDetailsId: payload => setState({type: SET_PRODUCT_DETAILS, payload}),
    setActiveBundleId: payload => setState({type: SET_ACTIVE_BUNDLE_ID, payload}),
    setActiveTabId: payload => setState({type: SET_ACTIVE_TAB_ID, payload}),
    setDefaultPresets: payload => setState({type: SET_DEFAULT_PRESETS, payload}),
    toggleStream: payload => setState({
      type: TOGGLE_STREAM,
      payload: typeof payload === 'boolean' ? payload : !state.isAllowStream,
    }),
    toggleConfiguratorPortal: payload => setState({
      type: TOGGLE_CONFIGURATION_PORTAL,
      payload: typeof payload === 'boolean' ? payload : !state.isConfiguratorPortalOpen,
    }),
    toggleProductPortal: payload => setState({
      type: TOGGLE_PRODUCT_PORTAL,
      payload: typeof payload === 'boolean' ? payload : !state.isProductPortalOpen,
    }),
    toggleInteractionsPortal: payload => setState({
      type: TOGGLE_INTERACTIONS_PORTAL,
      payload: typeof payload === 'boolean' ? payload : !state.isInteractionsPortalOpen,
    }),
    toggleStickyPanel: payload => setState({
      type: TOGGLE_STICKY_PANEL,
      payload: typeof payload === 'boolean' ? payload : !state.isStickyPanelCollapsed,
    }),
    toggleProductsDetails: payload => setState({
      type: TOGGLE_PRODUCT_DETAILS,
      payload: typeof payload === 'boolean' ? payload : !state.isInteractionsPortalOpen,
    }),
    getBundleById: id => state.bundles.find(b => b.bundleId === id),
    getBundleTitleById: () => {
      const activeBundle = scope.getActiveBundle();
      if(activeBundle) {
        return `${TEXT_GOMLVL_LONG} | ${activeBundle.title}`
      }
      return TEXT_GOMLVL_LONG;
    },
    getActiveBundle: () => scope.getBundleById(state.activeBundleId),
    getAllSelections: () => {
      if (!state.activeBundleId) return [];
      const bundle = scope.getBundleById(state.activeBundleId);
      const {options: {base, additional}} = bundle;
      
      return [...base, ...additional];
    },
    getRedirectLink: () => {
      /*
      * 11 point is a major note from marketing department. Take a look an initial feedback:
      * https://mifcom.atlassian.net/wiki/spaces/~697213956/pages/82477119/Feedback+GOML+V1
      * */
      return REDIRECT_LINK;
      
      const {activeBundleId, selectedOptions = {}, options} = state;
      const activeBundle = scope.getBundleById(activeBundleId);
      if(activeBundle) {
        const {source} = activeBundle;
        return Object.values(selectedOptions || {})
          .reduce((acc, productId) => {
            const isHardcoded = isHardCoded(productId);
            if(isHardcoded) return acc;
            const option = options.find(el => el.productId === productId);
            if (option) {
              acc += `&${option.optionId}=${option.selectionId}`;
            }
            return acc;
          }, makeConfiguratorLink(source))
      }
      return '';
    },
    getTotalPrice: () => {
      const {options, selectedOptions = {}} = state;
      if (!options.length) return 0;
      try {
        const {basePrice, options: {base}} = scope.getActiveBundle(); /*@TODO Should test*/
        return basePrice;
        const uniq = xor(base, Object.values(selectedOptions));

        if (!uniq.length) return basePrice;

        return uniq.reduce((acc, productId) => {
          const {price} = options.find(el => el.productId === productId);
          return ~base.indexOf(productId) ? acc -= price : acc += price;
        }, basePrice);
      } catch (e) {
        return 0;
      }
    },
  }

  window.scope = scope;

  return (
    <AppContext.Provider value={scope}>
      {children}
    </AppContext.Provider>
  )
}

export default AppContext;

Provider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
}
